import { Fragment, useState } from "react";
import { Trash2, ChevronDown, Download } from "lucide-react";
import { fmt } from "../../lib/api";
import { testProviderLabel } from "../../lib/servers";
import { useSort, SortTh } from "../../lib/useSort";

const LOG_COLS = [["created_at", "Time", (t) => t.created_at], ["rep_name", "Phone", (t) => t.rep_name], ["team", "Province", (t) => t.team],
  ["city", "Location", (t) => t.location?.city], ["isp", "ISP", (t) => t.isp], ["provider", "Test provider", testProviderLabel],
  ["download_mbps", "↓ Mbps", (t) => t.download_mbps], ["upload_mbps", "↑ Mbps", (t) => t.upload_mbps], ["ping_ms", "Ping", (t) => t.ping_ms],
  ["jitter_ms", "Jitter", (t) => t.jitter_ms], ["status", "Status", (t) => t.issues.length]];
const LOG_ACC = Object.fromEntries(LOG_COLS.map(([k, , f]) => [k, f]));

const exportCsv = (tests) => {
  const cols = ["created_at", "rep_name", "team", "download_mbps", "upload_mbps", "ping_ms", "jitter_ms", "issues", "location.source", "location.lat", "location.lon", "location.accuracy", "location.city", "location.region", "location.country", "location.address", "ip", "isp", "test_server", "server_colo", "server_city", "failed_servers", "effective_type", "connection_type", "browser_downlink", "browser_rtt", "platform", "user_agent", "screen", "timezone", "notes"];
  const get = (t, c) => { const v = c.split(".").reduce((o, k) => o?.[k], t); return Array.isArray(v) ? v.join("; ") : v ?? ""; };
  const csv = [cols.join(","), ...tests.map((t) => cols.map((c) => `"${String(get(t, c)).replace(/"/g, '""')}"`).join(","))].join("\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  a.download = "speed-tests.csv"; a.click();
};

const Detail = ({ t }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-1 px-6 py-3 bg-[#121212] text-[11px] text-neutral-400">
    <div>Address: {t.location?.address || "—"}</div>
    <div>Coords: {t.location?.lat ? `${fmt(t.location.lat, 4)}, ${fmt(t.location.lon, 4)} ±${fmt(t.location.accuracy, 0)}m` : "—"}</div>
    <div>IP: {t.ip || "—"} (server saw {t.server_seen_ip || "—"})</div>
    <div>Network: {t.effective_type || "—"} {t.connection_type || ""} · browser est. {t.browser_downlink ?? "—"} Mbps / {t.browser_rtt ?? "—"} ms</div>
    <div className="text-neutral-200" data-testid={`test-detail-provider-${t.id}`}>Testing provider: {testProviderLabel(t)}{t.failed_servers?.length ? ` · failover from: ${t.failed_servers.join(", ")}` : ""}</div>
    <div>Device: {t.platform || "—"} · {t.screen} · {t.timezone}</div>
    <div>Data: ↓{fmt((t.bytes_downloaded || 0) / 1e6, 0)}MB ↑{fmt((t.bytes_uploaded || 0) / 1e6, 0)}MB in {fmt(t.duration_s)}s</div>
    <div className="md:col-span-2 truncate">UA: {t.user_agent}</div>
    {t.notes && <div className="md:col-span-4 text-neutral-300">Notes: {t.notes}</div>}
  </div>
);

export const TestLog = ({ tests, onDelete }) => {
  const [open, setOpen] = useState(null);
  const { sorted, sort, toggle } = useSort(tests, LOG_ACC);
  return (
    <section className="border-b border-white/10" data-testid="test-log">
      <div className="px-6 py-3 border-b border-white/10 flex justify-between items-center">
        <span className="label">Full test log</span>
        <button data-testid="export-csv-button" onClick={() => exportCsv(tests)} className="btn-ghost text-xs flex items-center gap-1"><Download className="w-3 h-3" /> CSV</button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="text-neutral-500 text-left">
            <tr><th />{LOG_COLS.map(([k, l]) => <SortTh key={k} k={k} label={l} sort={sort} toggle={toggle} className="px-4 py-2" />)}<th /></tr>
          </thead>
          <tbody>
            {sorted.map((t) => {
              const has = (k) => t.issues.some((i) => i.includes(k)) ? "text-[#FF3B30]" : "";
              return (
                <Fragment key={t.id}>
                  <tr className="border-t border-white/5 hover:bg-white/[0.03] cursor-pointer" onClick={() => setOpen(open === t.id ? null : t.id)} data-testid={`test-log-row-${t.id}`}>
                    <td className="px-4 py-2"><ChevronDown className={`w-3 h-3 transition-transform ${open === t.id ? "rotate-180" : ""}`} /></td>
                    <td className="px-4 py-2 text-neutral-500 whitespace-nowrap">{new Date(t.created_at).toLocaleString()}</td>
                    <td className="px-4 py-2 text-white">{t.rep_name}</td>
                    <td className="px-4 py-2">{t.team}</td>
                    <td className="px-4 py-2">{t.location?.city || "—"} <span className="text-neutral-600">({t.location?.source})</span></td>
                    <td className="px-4 py-2 max-w-[160px] truncate">{t.isp || "—"}</td>
                    <td className="px-4 py-2 whitespace-nowrap" data-testid={`test-log-server-${t.id}`}>{testProviderLabel(t)}{t.failed_servers?.length ? " ⚠" : ""}</td>
                    <td className={`px-4 py-2 ${has("download")}`}>{fmt(t.download_mbps)}</td>
                    <td className={`px-4 py-2 ${has("upload")}`}>{fmt(t.upload_mbps)}</td>
                    <td className={`px-4 py-2 ${has("ping")}`}>{fmt(t.ping_ms, 0)}</td>
                    <td className={`px-4 py-2 ${has("jitter")}`}>{fmt(t.jitter_ms, 0)}</td>
                    <td className="px-4 py-2">{t.issues.length ? <span className="bg-[#FF3B30] text-white px-2 py-0.5">ISSUE</span> : <span className="text-[#32D74B]">OK</span>}</td>
                    <td className="px-4 py-2">
                      <button data-testid={`delete-test-${t.id}`} onClick={(e) => { e.stopPropagation(); if (window.confirm("Delete this test?")) onDelete(t.id); }} className="text-neutral-600 hover:text-[#FF3B30]"><Trash2 className="w-3 h-3" /></button>
                    </td>
                  </tr>
                  {open === t.id && <tr><td colSpan={13}><Detail t={t} /></td></tr>}
                </Fragment>
              );
            })}
          </tbody>
        </table>
        {!tests.length && <div className="px-6 py-4 text-xs text-neutral-600">No tests logged yet.</div>}
      </div>
    </section>
  );
};
